import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import type { GarageServiceOfferingResponse } from '../types';
import type { AdminOfferDetail } from '../types/offers';
import OfferStatusChip from '../components/offers/OfferStatusChip';
import OfferVariantPrices from '../components/offers/OfferVariantPrices';
import AdminBannerUploader from '../components/offers/AdminBannerUploader';
import { decisionError, STATUS_LABELS } from '../components/offers/offerReviewModel';
import {
  FIXED_MIN, MAX_BOOKINGS_MAX, PERCENT_MAX, PERCENT_MIN, START_LOCKED_STATUSES, SUBTITLE_MAX, TITLE_MAX,
  applyDefaultToAll, changedFields, confirmSummary, draftRule, editGate, formFromOffer, formToRequest, itemField, mapEditError,
  setItemValue, todayInDubai, toggleItem, validateEdit, minEndDate,
  type EditField, type EditFieldErrors, type OfferEditForm,
} from '../components/offers/offerEditModel';

interface ServiceOption {
  id: string;
  name: string;
  inactive?: boolean;
}

/**
 * #997 — `/service-approvals/offers/:id/edit`: an admin edits a garage offer in place. The offer keeps
 * its status (no re-approval); the garage is notified. Mirrors the partner `OfferEditorPage` fields.
 */
export default function OfferEditPage() {
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const today = useMemo(() => todayInDubai(), []);
  const reviewPath = `/service-approvals/offers/${id}`;

  const [detail, setDetail] = useState<AdminOfferDetail | null>(null);
  const [original, setOriginal] = useState<OfferEditForm | null>(null);
  const [form, setForm] = useState<OfferEditForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [services, setServices] = useState<ServiceOption[] | null>(null);
  const [servicesError, setServicesError] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<EditFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [needsReload, setNeedsReload] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!auth.token || !id) return;
    setLoading(true);
    setLoadError(null);
    try {
      const d = await api.getOfferForReview(id, auth.token);
      const f = formFromOffer(d.offer);
      setDetail(d);
      setOriginal(f);
      setForm(f);
      setFieldErrors({});
      setFormError(null);
      setNeedsReload(false);
      setBlocked(false);
    } catch (err) {
      setLoadError(decisionError(err, 'approve').message);
    } finally {
      setLoading(false);
    }
  }, [auth.token, id]);

  useEffect(() => { load(); }, [load]);

  const garageId = detail?.garage.id;
  useEffect(() => {
    if (!auth.token || !garageId) return;
    let cancelled = false;
    setServicesError(false);
    api.getGarageServices(garageId, auth.token)
      .then((list: GarageServiceOfferingResponse[]) => {
        if (cancelled) return;
        setServices((list ?? [])
          .filter((s) => s.status === 'ACTIVE' && !s.blockedAt)
          .map((s) => ({ id: s.id, name: s.name ?? s.category?.name ?? 'Service' })));
      })
      .catch(() => { if (!cancelled) setServicesError(true); });
    return () => { cancelled = true; };
  }, [auth.token, garageId]);

  const offer = detail?.offer ?? null;

  // Options = the garage's active services + any covered one that is no longer active (so it can be removed).
  const options: ServiceOption[] = useMemo(() => {
    const base = services ?? [];
    const known = new Set(base.map((s) => s.id));
    const extra = (offer?.items ?? [])
      .filter((i) => !known.has(i.offeringId))
      .map((i) => ({ id: i.offeringId, name: i.name, inactive: true }));
    return [...base, ...extra];
  }, [services, offer]);

  const draft = useMemo(() => (form ? draftRule(form) : null), [form]);
  const nameOf = useCallback(
    (offeringId: string) => options.find((o) => o.id === offeringId)?.name
      ?? offer?.items.find((i) => i.offeringId === offeringId)?.name ?? 'Service',
    [options, offer],
  );
  /** Spec 073: a covered service's server "from" price (FIXED must stay AED 1 below it). */
  const priceOf = useCallback(
    (offeringId: string) => offer?.items.find((i) => i.offeringId === offeringId)?.listPrice ?? null,
    [offer],
  );
  const changes = useMemo(() => (form && original ? changedFields(original, form, nameOf) : []), [form, original, nameOf]);

  if (loading && !detail) {
    return (
      <div className="admin-loading">
        <div className="admin-loading-spinner"></div>
      </div>
    );
  }

  if (!detail || !offer || !form || !original) {
    return (
      <div>
        <Link to={reviewPath} className="admin-link">← Back to offer</Link>
        <div className="admin-info-banner danger" role="alert" style={{ marginTop: '1rem' }}>
          {loadError ?? 'Could not load this offer.'}
        </div>
      </div>
    );
  }

  const gate = editGate(offer);
  const token = auth.token!;
  const startLocked = START_LOCKED_STATUSES.includes(offer.status);
  const disabled = saving || blocked || !gate.allowed;

  const clearErrors = (fields: EditField[]) => {
    setFieldErrors((e) => {
      const next = { ...e };
      fields.forEach((k) => { delete next[k]; });
      return next;
    });
  };

  const update = <K extends keyof OfferEditForm>(key: K, value: OfferEditForm[K], errorFields?: EditField[]) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
    setFormError(null);
    clearErrors(errorFields ?? [key as EditField]);
  };

  /** Spec 073: per-service edits. */
  const editItems = (next: (f: OfferEditForm) => OfferEditForm, fields: EditField[]) => {
    setForm((f) => (f ? next(f) : f));
    setFormError(null);
    clearErrors(fields);
  };
  const allItemFields = (): EditField[] => form.items.map((i) => itemField(i.offeringId));
  const percent = form.discountType === 'PERCENT';
  const unit = percent ? '%' : 'AED';

  const review = () => {
    const errs = validateEdit(form, { originalStartsAt: original.startsAt, today, bookingsUsed: offer.bookingsUsed ?? 0, priceOf });
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      setFormError('Fix the highlighted fields.');
      return;
    }
    setConfirming(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.updateOfferAsAdmin(offer.id, formToRequest(form, offer.version), token);
      navigate(reviewPath, { state: { toast: 'Offer updated. The garage has been notified.' } });
    } catch (err) {
      // Per-service errors come back as items[<i>] in the order formToRequest sent them.
      const mapped = mapEditError(err, form.items.map((i) => i.offeringId));
      setConfirming(false);
      setFieldErrors(mapped.fieldErrors);
      setFormError(mapped.formError);
      setNeedsReload(mapped.reload);
      setBlocked(mapped.blocked);
    } finally {
      setSaving(false);
    }
  };

  const err = (f: EditField) => (fieldErrors[f]
    ? <div className="offer-edit__error" id={`offer-edit-err-${f}`} role="alert">{fieldErrors[f]}</div>
    : null);
  const errProps = (f: EditField) => (fieldErrors[f]
    ? { 'aria-invalid': true as const, 'aria-describedby': `offer-edit-err-${f}` }
    : {});

  const textField = (key: 'titleEn' | 'titleAr' | 'subtitleEn' | 'subtitleAr', label: string, max: number, rtl = false) => (
    <div className="admin-form-group">
      <label className="admin-form-label" htmlFor={`offer-edit-${key}`}>{label}</label>
      <input
        id={`offer-edit-${key}`}
        className="admin-form-input"
        type="text"
        dir={rtl ? 'rtl' : 'ltr'}
        lang={rtl ? 'ar' : undefined}
        maxLength={max}
        value={form[key]}
        disabled={disabled}
        onChange={(e) => update(key, e.target.value)}
        {...errProps(key)}
      />
      <div className="offer-edit__counter">{form[key].length}/{max}</div>
      {err(key)}
    </div>
  );

  return (
    <div className="offer-review offer-edit">
      <div className="offer-review__header">
        <Link to={reviewPath} className="admin-link">← Back to offer</Link>
        <h2 className="offer-review__title">Edit offer</h2>
        <div className="offer-edit__subhead">
          <span className="admin-text-muted">{detail.garage.name}</span>
          <OfferStatusChip status={offer.status} />
        </div>
      </div>

      {!gate.allowed && gate.hint && (
        <div className="admin-info-banner warning" role="status" data-testid="offer-edit-blocked">{gate.hint}</div>
      )}
      {!gate.visible && (
        <div className="admin-info-banner warning" role="status" data-testid="offer-edit-blocked">
          {offer.revisionOf ? 'This is a pending change to a running offer. Approve or reject it instead.' : 'This offer can no longer be edited.'}
        </div>
      )}
      {gate.allowed && (
        <div className="admin-info-banner info" style={{ marginBottom: '1rem' }}>
          Saving applies at once and keeps the offer {STATUS_LABELS[offer.status].toLowerCase()}. The garage is notified of what changed.
        </div>
      )}

      {formError && (
        <div className="admin-info-banner danger" role="alert" data-testid="offer-edit-error" style={{ marginBottom: '1rem' }}>
          {formError}
          {needsReload && (
            <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" style={{ marginLeft: '0.75rem' }} onClick={load}>
              Reload
            </button>
          )}
        </div>
      )}

      <div className="offer-edit__grid">
        <div className="offer-edit__form">
          <section className="admin-card" aria-labelledby="offer-edit-details">
            <div className="admin-card-header"><h3 className="admin-card-title" id="offer-edit-details">Details</h3></div>
            <div className="offer-edit__cols">
              {textField('titleEn', 'Title (English)', TITLE_MAX)}
              {textField('titleAr', 'Title (Arabic)', TITLE_MAX, true)}
              {textField('subtitleEn', 'Subtitle (English)', SUBTITLE_MAX)}
              {textField('subtitleAr', 'Subtitle (Arabic)', SUBTITLE_MAX, true)}
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="offer-edit-startsAt">Starts on</label>
                <input
                  id="offer-edit-startsAt"
                  className="admin-form-input"
                  type="date"
                  value={form.startsAt}
                  disabled={disabled || startLocked}
                  onChange={(e) => update('startsAt', e.target.value, ['startsAt', 'endsAt'])}
                  {...errProps('startsAt')}
                />
                {startLocked && <p className="offer-edit__hint">A running offer keeps its start date.</p>}
                {err('startsAt')}
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="offer-edit-endsAt">Ends on</label>
                <input
                  id="offer-edit-endsAt"
                  className="admin-form-input"
                  type="date"
                  value={form.endsAt}
                  min={minEndDate(form.startsAt, today)}
                  disabled={disabled}
                  onChange={(e) => update('endsAt', e.target.value)}
                  {...errProps('endsAt')}
                />
                {err('endsAt')}
              </div>
            </div>
          </section>

          <section className="admin-card" aria-labelledby="offer-edit-discount">
            <div className="admin-card-header"><h3 className="admin-card-title" id="offer-edit-discount">Discount and limit</h3></div>
            <fieldset className="offer-edit__fieldset" disabled={disabled}>
              <legend className="admin-form-label">Discount type</legend>
              <label className="offer-edit__radio">
                <input type="radio" name="offer-edit-discount-type" checked={percent}
                  onChange={() => update('discountType', 'PERCENT', ['discountValue', 'maxDiscountPerBooking', ...allItemFields()])} />
                Percentage
              </label>
              <label className="offer-edit__radio">
                <input type="radio" name="offer-edit-discount-type" checked={!percent}
                  onChange={() => update('discountType', 'FIXED', ['discountValue', 'maxDiscountPerBooking', ...allItemFields()])} />
                Fixed amount (AED)
              </label>
            </fieldset>
            <div className="offer-edit__cols">
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="offer-edit-defaultDiscount">
                  {percent ? 'Default discount (%)' : 'Default discount (AED)'}
                </label>
                <div className="offer-edit__default-row">
                  <input id="offer-edit-defaultDiscount" data-testid="offer-edit-default-discount" className="admin-form-input"
                    type="number" inputMode="decimal" step="any" min={percent ? PERCENT_MIN : FIXED_MIN} max={percent ? PERCENT_MAX : undefined}
                    value={form.defaultDiscount} disabled={disabled} aria-describedby="offer-edit-defaultDiscount-hint"
                    onChange={(e) => { const v = e.target.value; setForm((f) => (f ? { ...f, defaultDiscount: v } : f)); }} />
                  <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" data-testid="offer-edit-apply-all"
                    disabled={disabled || form.items.length === 0 || form.defaultDiscount.trim() === ''}
                    title="Copy the default discount to every covered service"
                    onClick={() => editItems(applyDefaultToAll, ['discountValue', ...allItemFields()])}>
                    Apply to all
                  </button>
                </div>
                <p className="offer-edit__hint" id="offer-edit-defaultDiscount-hint">
                  Fills in each service you tick. Each service keeps its own discount under Covered services.
                </p>
                {err('discountValue')}
              </div>
              {form.discountType === 'PERCENT' && (
                <div className="admin-form-group">
                  <label className="admin-form-label" htmlFor="offer-edit-maxDiscountPerBooking">Max discount per booking (AED, optional)</label>
                  <input id="offer-edit-maxDiscountPerBooking" className="admin-form-input" type="number" inputMode="decimal" step="any"
                    value={form.maxDiscountPerBooking} disabled={disabled}
                    onChange={(e) => update('maxDiscountPerBooking', e.target.value)} {...errProps('maxDiscountPerBooking')} />
                  {err('maxDiscountPerBooking')}
                </div>
              )}
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="offer-edit-maxBookings">Booking limit (optional)</label>
                <input id="offer-edit-maxBookings" className="admin-form-input" type="number" inputMode="numeric" step={1}
                  min={Math.max(1, offer.bookingsUsed ?? 1)} max={MAX_BOOKINGS_MAX}
                  value={form.maxBookings} disabled={disabled}
                  onChange={(e) => update('maxBookings', e.target.value)} {...errProps('maxBookings')} />
                <p className="offer-edit__hint">{offer.bookingsUsed ?? 0} used so far. Leave empty for no limit.</p>
                {err('maxBookings')}
              </div>
            </div>
          </section>

          <section className="admin-card" aria-labelledby="offer-edit-services">
            <div className="admin-card-header"><h3 className="admin-card-title" id="offer-edit-services">Covered services</h3></div>
            {services == null && !servicesError && <div className="admin-text-muted" role="status">Loading the garage's services…</div>}
            {servicesError && (
              <p className="offer-edit__hint" data-testid="offer-edit-services-readonly">
                Could not load the garage's services, so the covered services can't be changed here. Their discounts still can.
              </p>
            )}
            {(servicesError || services != null) && (
              <ul className="offer-edit__services offer-edit__services--valued" {...errProps('offeringIds')}>
                {(servicesError ? offer.items.map((i) => ({ id: i.offeringId, name: i.name, inactive: false })) : options).map((o) => {
                  const item = form.items.find((i) => i.offeringId === o.id);
                  const rowErrKey = itemField(o.id);
                  const rowErrId = `offer-edit-err-item-${o.id}`;
                  return (
                    <li key={o.id} className={`offer-edit__service-row${item ? ' is-checked' : ''}`} data-testid={`offer-edit-service-${o.id}`}>
                      {servicesError ? (
                        <span className="offer-edit__service offer-edit__service--locked">{o.name}</span>
                      ) : (
                        <label className="offer-edit__service">
                          <input
                            type="checkbox"
                            checked={!!item}
                            disabled={disabled}
                            onChange={() => editItems((f) => toggleItem(f, o.id), ['offeringIds', 'discountValue', rowErrKey])}
                          />
                          <span>{o.name}</span>
                          {o.inactive && <span className="admin-badge warning">Not active</span>}
                        </label>
                      )}
                      {item && (
                        <span className="offer-edit__value" dir="ltr">
                          <input
                            className="admin-form-input"
                            type="number"
                            inputMode="decimal"
                            step="any"
                            min={percent ? PERCENT_MIN : FIXED_MIN}
                            max={percent ? PERCENT_MAX : undefined}
                            value={item.discountValue}
                            disabled={disabled}
                            aria-label={`Discount for ${o.name}`}
                            data-testid={`offer-edit-item-value-${o.id}`}
                            onChange={(e) => editItems((f) => setItemValue(f, o.id, e.target.value), [rowErrKey, 'discountValue'])}
                            {...(fieldErrors[rowErrKey] ? { 'aria-invalid': true as const, 'aria-describedby': rowErrId } : {})}
                          />
                          <span className="offer-edit__unit" aria-hidden="true">{unit}</span>
                        </span>
                      )}
                      {fieldErrors[rowErrKey] && (
                        <div className="offer-edit__error offer-edit__row-error" id={rowErrId} role="alert" data-testid={`offer-edit-item-error-${o.id}`}>
                          {fieldErrors[rowErrKey]}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {err('offeringIds')}
          </section>

          <section className="admin-card" aria-labelledby="offer-edit-banners">
            <div className="admin-card-header"><h3 className="admin-card-title" id="offer-edit-banners">Banners</h3></div>
            <div className="offer-edit__cols">
              <AdminBannerUploader
                offerId={offer.id}
                token={token}
                label="Banner (English)"
                value={{ storageKey: form.bannerStorageKey, url: form.bannerUrl }}
                disabled={disabled}
                error={fieldErrors.banner}
                testId="offer-edit-banner-en"
                onChange={(v) => {
                  setForm((f) => (f ? { ...f, bannerStorageKey: v.storageKey, bannerUrl: v.url } : f));
                  setFieldErrors((e) => { const n = { ...e }; delete n.banner; return n; });
                }}
              />
              <AdminBannerUploader
                offerId={offer.id}
                token={token}
                label="Banner (Arabic, optional)"
                hint="Without it, the app shows the English banner in Arabic."
                value={{ storageKey: form.bannerArStorageKey, url: form.bannerArUrl }}
                removable
                disabled={disabled}
                error={fieldErrors.bannerAr}
                testId="offer-edit-banner-ar"
                onChange={(v) => {
                  setForm((f) => (f ? { ...f, bannerArStorageKey: v.storageKey, bannerArUrl: v.url } : f));
                  setFieldErrors((e) => { const n = { ...e }; delete n.bannerAr; return n; });
                }}
              />
            </div>
          </section>
        </div>

        <div className="offer-edit__side">
          <OfferVariantPrices offerId={offer.id} version={offer.version} token={token} draft={draft} />
        </div>
      </div>

      <div className="offer-edit__bar">
        <span className="admin-text-muted" data-testid="offer-edit-changes">
          {changes.length ? `Changed: ${changes.join(', ')}` : 'No changes yet'}
        </span>
        <div className="offer-edit__bar-actions">
          <Link to={reviewPath} className="admin-btn admin-btn-outline">Cancel</Link>
          <button type="button" className="admin-btn admin-btn-primary" disabled={disabled || changes.length === 0} onClick={review}>
            Review changes
          </button>
        </div>
      </div>

      {confirming && (
        <div className="admin-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="offer-edit-confirm-title">
          <div className="admin-modal">
            <div className="admin-modal-header"><h3 id="offer-edit-confirm-title">Save changes to this offer?</h3></div>
            <div className="admin-modal-body">
              <p style={{ margin: 0 }} data-testid="offer-edit-confirm-summary">{confirmSummary(changes, offer.status)}</p>
            </div>
            <div className="admin-modal-footer">
              <button type="button" className="admin-btn" onClick={() => setConfirming(false)} disabled={saving}>Cancel</button>
              <button type="button" className="admin-btn admin-btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
